import { useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import {
  IconLoader,
  Table,
  useFilters,
  useQueryState,
  useScroll,
  useGetList,
  usePrevious,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  currenciesListColumns,
  currenciesListColumnsLabels,
  currenciesListFiltersSchema,
  currenciesListSchema,
} from './variables/list';
import currenciesSelector from './store/slices/selector';
import { useLazyGetConfigurationCurrenciesQuery } from './store/services';
import { Currency, CurrencyItem } from './types';

import cn from './currencies.module.css';

const formatCurrenciesList = (list: CurrencyItem[]): CurrencyItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {};

    render.code = (
      <div className={!result.active ? cn.disabledCurrency : ''}>
        {result.code || '-'}
      </div>
    );
    render.name = (
      <div className={!result.active ? cn.disabledCurrency : ''}>
        {result.name}
      </div>
    );
    render.description = (
      <div className={!result.active ? cn.disabledCurrency : ''}>
        {result.description}
      </div>
    );
    render.decimals = (
      <div className={!result.active ? cn.disabledCurrency : ''}>
        {result.decimals}
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const CurrenciesList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { searchText, pagination } = useAppSelector(
    (state) => state.currencies.list,
  );

  const currencies = useAppSelector(currenciesSelector);

  const { filters, onFilterChange } = useQueryState(currenciesListSchema);

  const prevFilters = usePrevious(filters);

  const formattedData = useMemo(() => {
    const data = formatCurrenciesList(currencies);

    return data;
  }, [currencies, filters, searchText]);

  const [getConfigurationCurrenciesList, { isFetching, isLoading }] =
    useLazyGetConfigurationCurrenciesQuery();

  const { getListAction, setPage, onFilter, errorList } = useGetList<Currency>({
    getList: getConfigurationCurrenciesList,
    onFilterChange,
    schema: currenciesListSchema,
    onSortChange: () => {},
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: currenciesListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !currencies
    ) {
      getListAction({
        filters,
      });
    }
  }, [filters, prevFilters, currencies]);

  useEffect(() => {
    let title = 'Currencies';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.currenciesLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        sort={{}}
        title="Currencies"
        error={errorList}
        columns={currenciesListColumns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        onFilterChange={(filterValues) => onFilter(filterValues)}
        columnsLabels={currenciesListColumnsLabels}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        take="30"
        setPage={(data) => setPage({ ...data, filters })}
        pagination={pagination}
        clickable
      />
    );
  }

  return (
    <div className={cn.currenciesBottom} ref={scrollRef}>
      {content}
      <Outlet />
      <div id="currency-popup" />
    </div>
  );
};

export default CurrenciesList;
